'use strict';

// Contract workspace — the construction-contract hub. Refactored onto pict-section-workspace: the base owns
// the shell + Overview + the RecordDates embed; the ContractWorkspace subclass carries the bespoke Projects tab
// (FK children + add/remove/create-under-contract management) and the Bid Items list (the contract's bid items,
// each showing its Project when set). The line-item ↔ bid-item MAPPING is a project-scoped concern (line items
// are project-scoped) and lives on the Project workspace, not here. See [[ContractWorkspace-Provider.js]].

const libContractWorkspaceProvider = require('../providers/ContractWorkspace-Provider.js');

module.exports = {
	Identifier: 'ContractWorkspace',
	Ordinal: 221,
	ProviderClass: libContractWorkspaceProvider,
	Entity: { Name: 'Contract', IDField: 'IDContract', Collection: 'Contracts', GUIDField: 'GUIDContract' },
	Header:
	{
		Eyebrow: 'Contract',
		Title: (pRec) => pRec.Name || `Contract ${pRec.IDContract}`,
		Actions: [ { Label: 'Edit', Icon: 'fas fa-pen', Style: 'is-link', OnClick: 'goToEdit()' } ],
		ListRoute: '#/PSRS/Contract/List',
	},
	Tabs:
	[
		{ Key: 'overview', Label: 'Overview', Icon: 'fas fa-circle-info', Content: { kind: 'overview' } },
		{ Key: 'dates', Label: 'Important Dates', Icon: 'fas fa-calendar-day', Content: { kind: 'embed', ref: 'RecordDates', style: 'provider', args: [ 'Contract' ] } },
		{ Key: 'projects', Label: (pProv) => `Projects (${(pProv._projects || []).length})`, Icon: 'fas fa-folder-open', Content: { kind: 'render', ref: 'renderProjects' } },
		{ Key: 'biditems', Label: 'Bid Items', Icon: 'fas fa-gavel', Content: { kind: 'render', ref: 'renderBidItems' }, OnEnter: 'loadBidItemsAsync' },
	],
	FieldGroups:
	[
		{ Name: 'Identity', Fields: [
			{ Key: 'Name', Label: 'Contract Name', Wide: true },
			{ Key: 'Status', Label: 'Status' },
			{ Key: 'ExternalContractID', Label: 'External Contract ID' },
		] },
		{ Name: 'Project Numbers', Fields: [
			{ Key: 'PrimaryProjectNumber', Label: 'Primary' },
			{ Key: 'CountyProjectNumber', Label: 'County' },
			{ Key: 'StateProjectNumber', Label: 'State' },
			{ Key: 'FederalProjectNumber', Label: 'Federal' },
		] },
		{ Name: 'Route & Limits', Fields: [
			{ Key: 'RouteNumber', Label: 'Route #' },
			{ Key: 'HighwayNumber', Label: 'Highway #' },
			{ Key: 'PavingMaterial', Label: 'Paving Material' },
			{ Key: 'StationBegin', Label: 'Station Begin' },
			{ Key: 'TerminusBegin', Label: 'Terminus Begin' },
			{ Key: 'StationEnd', Label: 'Station End' },
			{ Key: 'TerminusEnd', Label: 'Terminus End' },
		] },
		{ Name: 'Financials', Fields: [
			{ Key: 'TotalBidAmount', Label: 'Total Bid Amount', Format: 'currency' },
			{ Key: 'NetChangeOrderAmount', Label: 'Net Change Orders', Format: 'currency' },
			{ Key: 'VendorAccountCode', Label: 'Vendor Account Code' },
		] },
		{ Name: 'Specifications', Fields: [
			{ Key: 'ItemSpecificationYear', Label: 'Item Spec Year' },
			{ Key: 'MaterialsSpecificationYear', Label: 'Materials Spec Year' },
			{ Key: 'ItemIDTestSpecificationSet', Label: 'Item Test Spec Set', Format: 'id' },
			{ Key: 'MaterialIDTestSpecificationSet', Label: 'Material Test Spec Set', Format: 'id' },
			{ Key: 'DBEStructure', Label: 'DBE Structure' },
		] },
		{ Name: 'Relationships & Schedule', Fields: [
			{ Key: 'IDOrganization', Label: 'Prime Contractor', Card: 'Organization' },
			{ Key: 'IDContractType', Label: 'Contract Type', Card: 'ContractType' },
			{ Key: 'IDCustomer', Label: 'Customer', Card: 'Customer' },
			{ Key: 'EstimatedCompletionDate', Label: 'Estimated Completion', Format: 'date' },
		] },
		{ Name: 'Audit', Fields: [
			{ Key: 'CreateDate', Label: 'Created', Format: 'date' },
			{ Key: 'UpdateDate', Label: 'Last Updated', Format: 'date' },
			{ Key: 'GUIDContract', Label: 'GUID', Mono: true },
			{ Key: 'IDContract', Label: 'Contract ID', Format: 'id' },
		] },
	],
	CSSExtra: /*css*/`
		.cw-modal-help { font-size: 0.86rem; color: var(--theme-color-text-secondary, #5b6673); margin: 0 0 0.7rem; }
		.cw-addproj-list { max-height: 50vh; overflow-y: auto; margin-top: 0.6rem; border: 1px solid var(--theme-color-border-light, #e8ebf0); border-radius: 8px; }
		.cw-addproj-row { display: flex; align-items: center; justify-content: space-between; gap: 0.6rem; width: 100%; text-align: left; border: none; border-bottom: 1px solid var(--theme-color-border-light, #e8ebf0); background: var(--theme-color-background-panel, #fff); padding: 0.55rem 0.8rem; cursor: pointer; font: inherit; color: var(--theme-color-text-primary, #1f2733); }
		.cw-addproj-row:last-child { border-bottom: none; }
		.cw-addproj-row:hover { background: var(--theme-color-background-secondary, #f0f2f6); }
		.cw-addproj-name { font-size: 0.9rem; }
		.cw-addproj-tag { font-size: 0.66rem; text-transform: uppercase; letter-spacing: 0.03em; color: var(--theme-color-text-muted, #8a94a3); flex: none; }
		.cw-addproj-note { padding: 0.9rem; text-align: center; color: var(--theme-color-text-muted, #8a94a3); font-size: 0.86rem; }
		.cw-addproj-err { color: var(--theme-color-status-error, #cf5b5b); }
	`,
};
